import { ProductBrand } from '@/app/ProductBrand'
import { type EntryLearner } from '@/platform/access/learner-session'

import { useFocusOnMount } from '../learning/focus'
import { LearnerAvatar } from './entry-ui'

/**
 * Screen C (D-16): the normal entry after the parent's login. Each learner of
 * this family picks their own profile; the parent has a separate entry that
 * still needs the parent PIN. Only the family's own learners are listed (the
 * server returns them for the signed-in family only).
 */

export interface LearnerSelectScreenProps {
  readonly learners: readonly EntryLearner[]
  readonly onPick: (learner: EntryLearner) => void
  readonly onParent: () => void
}

export function LearnerSelectScreen({
  learners,
  onPick,
  onParent,
}: LearnerSelectScreenProps): React.JSX.Element {
  const heading = useFocusOnMount<HTMLHeadingElement>()
  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-3xl flex-col gap-6 px-5 pt-4 pb-10">
      <div className="flex items-center justify-between gap-3">
        <ProductBrand className="text-slate-700 dark:text-slate-300" />
        <button
          type="button"
          className="flex h-11 items-center gap-2 rounded-full border border-slate-300 px-4 text-base text-slate-700 dark:border-slate-700 dark:text-slate-300"
          onClick={onParent}
        >
          <span aria-hidden>🔒</span> Bố mẹ
        </button>
      </div>

      <h1 ref={heading} tabIndex={-1} className="text-3xl font-bold">
        Ai đang học?
      </h1>

      <ul aria-label="Chọn hồ sơ" className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        {learners.map((learner) => (
          <li key={learner.learner_id}>
            <button
              type="button"
              disabled={!learner.pin_configured}
              aria-describedby={
                learner.pin_configured ? undefined : `no-pin-${learner.learner_id}`
              }
              className="flex w-full flex-col items-center gap-3 rounded-3xl border-2 border-slate-200 bg-white p-5 active:bg-slate-50 disabled:opacity-60 dark:border-slate-800 dark:bg-slate-900 dark:active:bg-slate-800"
              onClick={() => {
                onPick(learner)
              }}
            >
              <LearnerAvatar id={learner.learner_id} nickname={learner.nickname} />
              <span className="max-w-full truncate text-xl font-semibold">
                {learner.nickname}
              </span>
              {!learner.pin_configured && (
                <span
                  id={`no-pin-${learner.learner_id}`}
                  className="text-center text-base text-slate-600 dark:text-slate-400"
                >
                  Chưa có mã PIN. Nhờ bố mẹ đặt nhé.
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </main>
  )
}
